import { useFetchShippingAddresses } from "@sushicorp/services";
import { ShippingAddress } from "artisn/types";
import React, { useState } from "react";
import { flushSync } from "react-dom";

import Styles from "./ProfileAddresses.styles";
import { ProfileAddressesProps as Props } from "./ProfileAddresses.types";
import AddNewRecordButton from "components/global/AddNewRecordButton/AddNewRecordButton";
import Footer from "components/global/Footer/Footer";
import GoBack from "components/global/GoBack/GoBack";
import Navbar from "components/global/Navbar/Navbar";
import ProfileNavigation from "components/global/ProfileNavigation/ProfileNavigation";
import Redirect from "components/global/Redirect/Redirect";
import AddressFormModal from "components/profileAddresses/AddressFormModal/AddressFormModal";
import AddressList from "components/profileAddresses/AddressList/AddressList";
import CONSTANTS from "config/constants";
import useAuth from "contexts/auth/auth.context.hooks";
import useI18n from "hooks/useI18n";
import { notify } from "utils/common.utils";
import { dismissErrorNotification } from "utils/notifications.utils";
import { createErrorNotification } from "utils/notifications.utils";

const { MAX_NUMBER_OF_SHIPPING_ADDRESSES } = CONSTANTS.CONSTRAINTS;
const { WITH_BOTTOM_SEARCH, WITH_BOTTOM_RESPONSIVE_BUTTONS } =
  CONSTANTS.FEATURE_FLAGS;

const ProfileAddresses: React.FC<Props> = props => {
  const t = useI18n();
  const [openModal, setOpenModal] = useState(false);
  const [editAddress, setEditAddress] = useState<ShippingAddress>();
  const auth = useAuth();
  const { isAnonymous } = auth;
  const { data: addressList } = useFetchShippingAddresses(auth, notify);

  const editAddressHandler = (address: ShippingAddress) => {
    flushSync(() => {
      setEditAddress(address);
    });
    setOpenModal(true);
  };

  const closeHandler = () => {
    setEditAddress(undefined);
    setOpenModal(false);
  };

  const validateNumberAddresses = () => {
    if ((addressList?.length ?? 0) >= MAX_NUMBER_OF_SHIPPING_ADDRESSES) {
      dismissErrorNotification();
      const maxAddressError = t.errors.maxAddressLimit(
        MAX_NUMBER_OF_SHIPPING_ADDRESSES
      );
      createErrorNotification(maxAddressError, t.errors.overAddressLimit);
      return null;
    }
    setOpenModal(true);
  };

  if (isAnonymous) {
    return <Redirect pathname="/signin" redirect="/profile/addresses" />;
  }

  return (
    <Styles
      className="ProfileAddresses"
      withBottomSearch={WITH_BOTTOM_SEARCH}
      withBottomResponsiveButtons={WITH_BOTTOM_RESPONSIVE_BUTTONS}
    >
      <header className="ProfileAddresses__header">
        <Navbar showBrand />
      </header>
      <main className="ProfileAddresses__main">
        <div className="ProfileAddresses__layout">
          <ProfileNavigation className="ProfileAddresses__navigation" />
          <div className="ProfileAddresses__container">
            <GoBack
              className="ProfileAddresses__go-back"
              title={t.common.goBack}
            />
            <div className="ProfileAddresses__wrapper">
              <div className="ProfileAddresses__wrapper__header">
                <p className="ProfileAddresses__wrapper__title">
                  {t.profile.navigation.deliveryAddress}
                </p>
                <AddNewRecordButton
                  onClick={validateNumberAddresses}
                  value={t.profile.address.addNewAddress}
                  mobileValue={t.common.add}
                  color="primary"
                />
              </div>
              <AddressList
                actions={["delete", "edit"]}
                onEdit={editAddressHandler}
                className="ProfileAddresses__address-list"
                dropdownFixed={false}
              />
              <AddressFormModal
                opened={openModal}
                onClose={closeHandler}
                editAddress={editAddress}
              />
            </div>
          </div>
        </div>
      </main>
      <footer className="ProfileAddresses__footer">
        <Footer />
      </footer>
    </Styles>
  );
};

ProfileAddresses.defaultProps = {};

export default ProfileAddresses;
